import React, { useState } from 'react'
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  TextField,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Alert,
  Avatar,
  IconButton,
  Chip,
} from '@mui/material'
import {
  Save as SaveIcon,
  Security as SecurityIcon,
  Notifications as NotificationsIcon,
  Backup as BackupIcon,
  Delete as DeleteIcon,
  AccountCircle as AccountIcon,
  Business as BusinessIcon,
  AccessTime as AccessTimeIcon,
  People as PeopleIcon,
  Upload as UploadIcon,
  Restore as RestoreIcon,
  Add as AddIcon,
} from '@mui/icons-material'
import { useLoading } from '../../hooks'
import { StatusChip, RoleChip } from '../../components/common/Chips'
import { formatDate } from '../../utils/formatters'

// Mock data
const mockOrgSettings = {
  orgName: 'TechCorp Solutions',
  orgId: 'ORG-1001',
  industry: 'Technology',
  timezone: 'Asia/Kolkata',
  currency: 'INR',
  dateFormat: 'DD/MM/YYYY',
  autoApprove: true,
  requirePhoto: true,
  lateThreshold: 15,
  overtimeRate: 1.5,
  notifications: {
    email: true,
    push: true,
    sms: false,
    dailyReport: true,
    weeklyReport: true,
  },
}

const mockAdminUsers = [
  { id: 1, name: 'Admin User', email: 'admin@techcorp.com', role: 'superadmin', status: 'active' },
  { id: 2, name: 'HR Manager', email: 'hr@techcorp.com', role: 'hr', status: 'active' },
  { id: 3, name: 'IT Manager', email: 'it@techcorp.com', role: 'admin', status: 'inactive' },
]

const mockBackupHistory = [
  { id: 1, date: '2024-01-15', type: 'automatic', size: '2.5 GB', status: 'completed' },
  { id: 2, date: '2024-01-08', type: 'manual', size: '2.3 GB', status: 'completed' },
  { id: 3, date: '2024-01-01', type: 'automatic', size: '2.2 GB', status: 'failed' },
]

const Settings = () => {
  const [tabValue, setTabValue] = useState(0)
  const [settings, setSettings] = useState(mockOrgSettings)
  const { loading, startLoading, stopLoading } = useLoading()
  const [showSuccess, setShowSuccess] = useState(false)

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue)
  }

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }))
  }

  const handleNotificationChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        [key]: value
      }
    }))
  }

  const handleSaveSettings = () => {
    startLoading()
    // Simulate API call
    setTimeout(() => {
      stopLoading()
      setShowSuccess(true)
      setTimeout(() => setShowSuccess(false), 3000)
    }, 1000)
  }

  const handleCreateBackup = () => {
    // Create backup
  }

  const handleRestoreBackup = (backupId) => {
    if (window.confirm('Restore from this backup? All current data will be replaced.')) {
      // Restore backup
    }
  }

  const handleDeleteBackup = (backupId) => {
    if (window.confirm('Delete this backup?')) {
      // Delete backup
    }
  }

  const handleAddAdmin = () => {
    // Add new admin
  }

  const handleRemoveAdmin = (userId) => {
    if (window.confirm('Remove this admin user?')) {
      // Remove admin
    }
  }

  const handleOrgLogoUpload = (event) => {
    const file = event.target.files[0]
    if (file) {
      // Handle file upload
    }
  }

  return (
    <Box sx={{ p: { xs: 1, md: 3 } }}>
      <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, bgcolor: 'background.paper', boxShadow: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Organization Settings
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Manage your organization settings and preferences
          </Typography>
        </Box>

        {showSuccess && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Settings saved successfully!
          </Alert>
        )}

        {/* Tabs */}
        <Paper variant="outlined" sx={{ mb: 3 }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="General" />
            <Tab label="Attendance" />
            <Tab label="Notifications" />
            <Tab label="Administrators" />
            <Tab label="Backup & Restore" />
            <Tab label="Security" />
          </Tabs>
        </Paper>

        {/* Tab Content */}
        <Box>
          {tabValue === 0 && (
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 8 }}>
                <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
                    <BusinessIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Organization Information
                  </Typography>

                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Organization Name"
                        value={settings.orgName}
                        onChange={(e) => handleSettingChange('orgName', e.target.value)}
                        margin="normal"
                      />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Organization ID"
                        value={settings.orgId}
                        disabled
                        margin="normal"
                        helperText="Cannot be changed"
                      />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormControl fullWidth margin="normal">
                        <InputLabel>Industry</InputLabel>
                        <Select
                          label="Industry"
                          value={settings.industry}
                          onChange={(e) => handleSettingChange('industry', e.target.value)}
                        >
                          <MenuItem value="Technology">Technology</MenuItem>
                          <MenuItem value="Manufacturing">Manufacturing</MenuItem>
                          <MenuItem value="Healthcare">Healthcare</MenuItem>
                          <MenuItem value="Education">Education</MenuItem>
                          <MenuItem value="Retail">Retail</MenuItem>
                          <MenuItem value="Finance">Finance</MenuItem>
                          <MenuItem value="Other">Other</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormControl fullWidth margin="normal">
                        <InputLabel>Timezone</InputLabel>
                        <Select
                          label="Timezone"
                          value={settings.timezone}
                          onChange={(e) => handleSettingChange('timezone', e.target.value)}
                        >
                          <MenuItem value="Asia/Kolkata">India (IST)</MenuItem>
                          <MenuItem value="America/New_York">US Eastern (EST)</MenuItem>
                          <MenuItem value="Europe/London">UK (GMT)</MenuItem>
                          <MenuItem value="Asia/Singapore">Singapore (SGT)</MenuItem>
                          <MenuItem value="Australia/Sydney">Australia (AEST)</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormControl fullWidth margin="normal">
                        <InputLabel>Currency</InputLabel>
                        <Select
                          label="Currency"
                          value={settings.currency}
                          onChange={(e) => handleSettingChange('currency', e.target.value)}
                        >
                          <MenuItem value="INR">Indian Rupee (₹)</MenuItem>
                          <MenuItem value="USD">US Dollar ($)</MenuItem>
                          <MenuItem value="EUR">Euro (€)</MenuItem>
                          <MenuItem value="GBP">British Pound (£)</MenuItem>
                          <MenuItem value="JPY">Japanese Yen (¥)</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormControl fullWidth margin="normal">
                        <InputLabel>Date Format</InputLabel>
                        <Select
                          label="Date Format"
                          value={settings.dateFormat}
                          onChange={(e) => handleSettingChange('dateFormat', e.target.value)}
                        >
                          <MenuItem value="DD/MM/YYYY">DD/MM/YYYY</MenuItem>
                          <MenuItem value="MM/DD/YYYY">MM/DD/YYYY</MenuItem>
                          <MenuItem value="YYYY-MM-DD">YYYY-MM-DD</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                        <Switch
                          checked={settings.autoApprove}
                          onChange={(e) => handleSettingChange('autoApprove', e.target.checked)}
                        />
                        <Box>
                          <Typography>Auto-approve attendance</Typography>
                          <Typography variant="caption" color="textSecondary">
                            Automatically approve attendance without manual review
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 2, height: '100%' }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
                    Organization Logo
                  </Typography>

                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
                    <Avatar
                      sx={{ width: 120, height: 120, mb: 2, border: '2px solid #e0e0e0' }}
                    >
                      <BusinessIcon sx={{ fontSize: 60 }} />
                    </Avatar>

                    <input
                      accept="image/*"
                      style={{ display: 'none' }}
                      id="logo-upload"
                      type="file"
                      onChange={handleOrgLogoUpload}
                    />
                    <label htmlFor="logo-upload">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<UploadIcon />}
                      >
                        Upload Logo
                      </Button>
                    </label>

                    <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
                      Recommended: 500x500px, PNG or JPG
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Organization Status
                  </Typography>
                  <Chip label="Active" color="success" sx={{ mr: 1 }} />
                  <Chip label="Verified" color="info" />
                </Paper>
              </Grid>
            </Grid>
          )}

          {tabValue === 1 && (
            <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
                <AccessTimeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Attendance Settings
              </Typography>

              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Late Threshold (minutes)"
                    type="number"
                    value={settings.lateThreshold}
                    onChange={(e) => handleSettingChange('lateThreshold', parseInt(e.target.value))}
                    margin="normal"
                    helperText="Considered late if check-in exceeds this time"
                    InputProps={{ inputProps: { min: 0, max: 120 } }}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Overtime Rate"
                    type="number"
                    value={settings.overtimeRate}
                    onChange={(e) => handleSettingChange('overtimeRate', parseFloat(e.target.value))}
                    margin="normal"
                    helperText="Multiplier for overtime hours (e.g., 1.5 = time and a half)"
                    InputProps={{ inputProps: { min: 1, max: 3, step: 0.1 } }}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                    <Switch
                      checked={settings.requirePhoto}
                      onChange={(e) => handleSettingChange('requirePhoto', e.target.checked)}
                    />
                    <Box>
                      <Typography>Require face photo for check-in/out</Typography>
                      <Typography variant="caption" color="textSecondary">
                        Employees must take a photo for attendance verification
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Alert severity="info" sx={{ mt: 2 }}>
                    These settings affect all employees and attendance calculations.
                  </Alert>
                </Grid>
              </Grid>
            </Paper>
          )}

          {tabValue === 2 && (
            <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
                <NotificationsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Notification Settings
              </Typography>

              <List>
                <ListItem>
                  <ListItemText
                    primary="Email Notifications"
                    secondary="Receive important updates via email"
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={settings.notifications.email}
                      onChange={(e) => handleNotificationChange('email', e.target.checked)}
                    />
                  </ListItemSecondaryAction>
                </ListItem>

                <Divider />

                <ListItem>
                  <ListItemText
                    primary="Push Notifications"
                    secondary="Receive real-time notifications on your device"
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={settings.notifications.push}
                      onChange={(e) => handleNotificationChange('push', e.target.checked)}
                    />
                  </ListItemSecondaryAction>
                </ListItem>

                <Divider />

                <ListItem>
                  <ListItemText
                    primary="SMS Notifications"
                    secondary="Receive important alerts via SMS"
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={settings.notifications.sms}
                      onChange={(e) => handleNotificationChange('sms', e.target.checked)}
                    />
                  </ListItemSecondaryAction>
                </ListItem>

                <Divider />

                <ListItem>
                  <ListItemText
                    primary="Daily Attendance Report"
                    secondary="Receive daily attendance summary"
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={settings.notifications.dailyReport}
                      onChange={(e) => handleNotificationChange('dailyReport', e.target.checked)}
                    />
                  </ListItemSecondaryAction>
                </ListItem>

                <Divider />

                <ListItem>
                  <ListItemText
                    primary="Weekly Analytics Report"
                    secondary="Receive weekly attendance analytics"
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={settings.notifications.weeklyReport}
                      onChange={(e) => handleNotificationChange('weeklyReport', e.target.checked)}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              </List>
            </Paper>
          )}

          {tabValue === 3 && (
            <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
                <PeopleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Administrator Management
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddAdmin}
                >
                  Add Administrator
                </Button>
              </Box>

              <List>
                {mockAdminUsers.map((admin) => (
                  <React.Fragment key={admin.id}>
                    <ListItem>
                      <Avatar sx={{ mr: 2, bgcolor: '#1976d2' }}>
                        <AccountIcon />
                      </Avatar>
                      <ListItemText
                        primary={admin.name}
                        secondaryTypographyProps={{ component: 'div' }}
                        secondary={
                          <>
                            <Typography variant="caption" display="block">
                              {admin.email}
                            </Typography>
                            <RoleChip role={admin.role} size="small" />
                          </>
                        }
                      />
                      <ListItemSecondaryAction>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <StatusChip status={admin.status} size="small" />
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemoveAdmin(admin.id)}
                            disabled={admin.role === 'superadmin'}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </ListItemSecondaryAction>
                    </ListItem>
                    <Divider />
                  </React.Fragment>
                ))}
              </List>

              <Alert severity="info" sx={{ mt: 2 }}>
                Administrators have access to organization settings and employee data.
              </Alert>
            </Paper>
          )}

          {tabValue === 4 && (
            <Grid>
              <Grid size={{ xs: 12, md: 6 }} sx={{ mb: 3 }}>
                <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 2, height: '100%' }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
                    <BackupIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Backup Data
                  </Typography>

                  <Alert severity="warning" sx={{ mb: 3 }}>
                    Backups include all employee data, attendance records, and settings.
                  </Alert>

                  <Box sx={{ mb: 3 }}>
                    <Button
                      variant="contained"
                      startIcon={<BackupIcon />}
                      onClick={handleCreateBackup}
                      fullWidth
                      size="large"
                    >
                      Create Backup Now
                    </Button>
                  </Box>

                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Backup Schedule
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Automatic backups are created every Sunday at 2:00 AM.
                  </Typography>

                  <Alert severity="info">
                    Last backup: 2 days ago • Size: 2.5 GB
                  </Alert>
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 2, height: '100%' }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
                    <RestoreIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Restore History
                  </Typography>

                  <List>
                    {mockBackupHistory.map((backup) => (
                      <React.Fragment key={backup.id}>
                        <ListItem>
                          <ListItemText
                            primary={`Backup from ${formatDate(backup.date)}`}
                            secondaryTypographyProps={{ component: 'div' }}
                            secondary={
                              <>
                                <Typography variant="caption" display="block">
                                  Type: {backup.type} • Size: {backup.size}
                                </Typography>
                                <Box sx={{ mt: 0.5 }}>
                                  <StatusChip status={backup.status} size="small" />
                                </Box>
                              </>
                            }
                          />
                          <ListItemSecondaryAction>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleRestoreBackup(backup.id)}
                                disabled={backup.status !== 'completed'}
                              >
                                <RestoreIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteBackup(backup.id)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </ListItemSecondaryAction>
                        </ListItem>
                        <Divider />
                      </React.Fragment>
                    ))}
                  </List>
                </Paper>
              </Grid>
            </Grid>
          )}

          {tabValue === 5 && (
            <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
                <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Security Settings
              </Typography>

              <Grid container spacing={3}>
                <Grid size={{ xs: 12 }}>
                  <Alert severity="info" sx={{ mb: 3 }}>
                    These settings affect security and access control for your organization.
                  </Alert>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Session Timeout (minutes)"
                    type="number"
                    defaultValue="30"
                    margin="normal"
                    helperText="Auto-logout after inactivity"
                    InputProps={{ inputProps: { min: 5, max: 240 } }}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Max Login Attempts"
                    type="number"
                    defaultValue="5"
                    margin="normal"
                    helperText="Account lock after failed attempts"
                    InputProps={{ inputProps: { min: 1, max: 10 } }}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Alert severity="warning">
                    Changing security settings may affect user access. Proceed with caution.
                  </Alert>
                </Grid>
              </Grid>
            </Paper>
          )}
        </Box>

        {/* Save Button */}
        {tabValue < 4 && ( // Don't show save button for backup and security tabs
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSaveSettings}
              disabled={loading}
              size="large"
            >
              {loading ? 'Saving...' : 'Save Settings'}
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  )
}

export default Settings